import { useEffect, useState } from "react";
import { Download, Printer } from "lucide-react";
import { api, brl } from "@/lib/api";
import { useAdminTheme } from "./AdminLayout";
import { FilterBar, useAdminFilters, cityParam } from "./filters";
import { Button } from "@/components/ui/button";
import { MageCityLogo, TimeFoodLogo } from "@/components/Brand";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const PERIOD_LABEL = { day: "Hoje", week: "Últimos 7 dias", month: "Últimos 30 dias" };

export default function AdminReport() {
  const { accent } = useAdminTheme();
  const f = useAdminFilters();
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/admin/municipal-report", { params: { city_id: cityParam(f) || undefined, period: f.period } })
      .then(({ data }) => setD(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.cityId, f.period]);

  const exportCsv = () => {
    if (!d) return;
    const lines = [
      "Relatorio de arrecadacao municipal - Time Food",
      `Periodo;${PERIOD_LABEL[d.period]}`,
      `Pedidos;${d.orders_count}`,
      `Volume transacionado;${d.gmv.toFixed(2)}`,
      `Arrecadacao municipal total;${d.total_municipal_fee.toFixed(2)}`,
      "",
      "Loja;Pedidos;Volume;Arrecadacao municipal",
      ...d.by_store.map((s) => `${s.store_name};${s.orders};${s.gmv.toFixed(2)};${s.municipal_fee.toFixed(2)}`),
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `arrecadacao-municipal-${d.period}.csv`;
    a.click();
  };

  return (
    <div data-testid="admin-report">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold">Relatório de arrecadação municipal</h1>
          <p className="text-sm text-gray-500">Documento pensado para apresentação ao gestor público</p>
        </div>
        <FilterBar f={f} accent={accent} />
      </div>

      {!d ? <p className="text-sm text-gray-500 mt-8">Carregando…</p> : (
        <div className="rounded-2xl border border-gray-200 bg-white mt-6 overflow-hidden" data-testid="report-document">
          <div className="p-6 border-b-4" style={{ borderColor: "#2E8B57" }}>
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-4">
                <MageCityLogo size={54} />
                <div className="h-12 w-px bg-gray-300" />
                <TimeFoodLogo size={26} />
              </div>
              <div className="text-right">
                <p className="text-[11px] uppercase font-bold tracking-wider text-gray-400">Relatório oficial</p>
                <p className="font-display font-extrabold text-mage-navy">
                  {d.city?.brand_name || "Magé Time Food"}
                </p>
                <p className="text-[11px] text-gray-500">
                  {PERIOD_LABEL[d.period]} · gerado em {new Date(d.generated_at).toLocaleString("pt-BR")}
                </p>
              </div>
            </div>
          </div>

          <div className="grid sm:grid-cols-3 gap-3 p-6">
            <Big label="Pedidos no período" value={d.orders_count} testId="report-orders" />
            <Big label="Volume transacionado" value={brl(d.gmv)} testId="report-gmv" />
            <Big
              label={`Arrecadação municipal (${d.city?.municipal_fee_pct ?? 3.5}%)`}
              value={brl(d.total_municipal_fee)}
              testId="report-municipal-total"
              highlight
            />
          </div>

          <div className="px-6 pb-2">
            <h2 className="font-display font-bold text-lg">Arrecadação por estabelecimento</h2>
          </div>
          <div className="overflow-x-auto">
            <Table data-testid="report-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Estabelecimento</TableHead>
                  <TableHead className="text-right">Pedidos</TableHead>
                  <TableHead className="text-right">Volume</TableHead>
                  <TableHead className="text-right">Arrecadação municipal</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {d.by_store.map((s) => (
                  <TableRow key={s.store_name}>
                    <TableCell className="font-semibold">{s.store_name}</TableCell>
                    <TableCell className="text-right">{s.orders}</TableCell>
                    <TableCell className="text-right">{brl(s.gmv)}</TableCell>
                    <TableCell className="text-right font-display font-extrabold text-mage-navy">{brl(s.municipal_fee)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="p-6 flex flex-wrap gap-2 items-center border-t border-gray-200 mt-4">
            <Button data-testid="export-csv-button" onClick={exportCsv} className="h-11 rounded-xl font-bold text-white" style={{ background: accent }}>
              <Download size={16} className="mr-2" /> Exportar CSV
            </Button>
            <Button data-testid="print-report-button" variant="outline" onClick={() => window.print()} className="h-11 rounded-xl font-bold">
              <Printer size={16} className="mr-2" /> Imprimir / PDF
            </Button>
            <p className="text-[11px] text-gray-400 ml-auto">
              Magé Time Food — Parceria oficial Prefeitura de Magé · software Time Food
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

const Big = ({ label, value, highlight, testId }) => (
  <div className="rounded-2xl border p-5" style={{ borderColor: highlight ? "#1F3864" : "#E5E7EB", background: highlight ? "rgba(31,56,100,.04)" : "#fff" }}>
    <p className="text-[11px] text-gray-500">{label}</p>
    <p data-testid={testId} className="font-display text-2xl font-extrabold" style={{ color: highlight ? "#1F3864" : "#141414" }}>
      {value}
    </p>
  </div>
);
